"use client"

import { Fragment, useState } from "react"
import CustomDatePicker from "../../../utils/date-picker/CustomDatePicker"
import Buttons from "../../../utils/buttons/Buttons"
import CustomModal from "../../../utils/modals/CustomModal"
import Icons from "../../../utils/Icons"

const DateRangePicker = ({ label, startDate, setStartDate, endDate, setEndDate, onSelect }) => (
  <div className="row mb-3">
    <div className="col-sm-6">
      <label className="form-label fw-semibold">{label} (From)</label>
      <CustomDatePicker
        value={startDate}
        setValue={(date) => {
          setStartDate(date)
          onSelect(label)
        }}
        className="form-control rounded-lg border-gray-300 shadow-sm"
      />
    </div>
    <div className="col-sm-6">
      <label className="form-label fw-semibold">{label} (To)</label>
      <CustomDatePicker
        value={endDate}
        setValue={setEndDate}
        minDate={startDate}
        className="form-control rounded-lg border-gray-300 shadow-sm"
      />
    </div>
  </div>
)

const ResultDropdown = ({ value, onChange }) => (
  <div className="row mb-3">
    <div className="col-12">
      <label className="form-label fw-semibold">Result</label>
      <select
        className="form-select rounded-lg border-gray-300 shadow-sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">Select Result</option>
        <option value="pass">Pass</option>
        <option value="fail">Fail</option>
        <option value="dissatisfied">Dissatisfied</option>
      </select>
    </div>
  </div>
)

const RangeDateFilter = ({ resultFilter,setResultFilter,setPageData, getList, filter, setDateRanges, dateRanges }) => {
  const [isDateFilter, setIsDateFilter] = useState(false)

  const handleDateRangeSelect = (label) => {
    if (label !== "Client Deadline") setDateRanges((prev) => ({ ...prev, clientStartDate: null, clientEndDate: null }))
    if (label !== "Expert Deadline") setDateRanges((prev) => ({ ...prev, expertStartDate: null, expertEndDate: null }))
    if (label !== "Order Creation") setDateRanges((prev) => ({ ...prev, leadStartDate: null, leadEndDate: null }))
  }

  const clearFilter = () => {
    const initialValue = {
      clientStartDate: null,
      clientEndDate: null,
      expertStartDate: null,
      expertEndDate: null,
      leadStartDate: null,
      leadEndDate: null,
    }
    setDateRanges(initialValue)
    setResultFilter(null)
    getList({
      currentPage: 1,
      filter,
      isEmptylist: true,
      dateFilter: initialValue,
      resultFilter: null,
    })
    setIsDateFilter(false)
  }

  const validateDateRanges = () => {
    const { clientStartDate, clientEndDate, expertStartDate, expertEndDate, leadStartDate, leadEndDate } = dateRanges
    const isClientFilled = clientStartDate || clientEndDate
    const isExpertFilled = expertStartDate || expertEndDate
    const isLeadFilled = leadStartDate || leadEndDate

    if (
      (isClientFilled && (isExpertFilled || isLeadFilled)) ||
      (isExpertFilled && (isClientFilled || isLeadFilled)) ||
      (isLeadFilled && (isClientFilled || isExpertFilled))
    ) {
      alert("Please fill in only one date range at a time.")
      return false
    }
    return true
  }

  const applyFilter = () => {
    if (!validateDateRanges()) return
    setPageData({ page: 1 })
    getList({
      currentPage: 1,
      filter,
      dateFilter: dateRanges,
      resultFilter: resultFilter,
      firstTime: true,
    })
    setIsDateFilter(false)
  }

  const Content = () => (
    <Fragment>
      <div className="p-3 bg-white rounded-lg">
        <h5 className="text-center mb-4 font-bold text-gray-800">Filter Options</h5>

        <DateRangePicker
          label="Client Deadline"
          startDate={dateRanges.clientStartDate}
          setStartDate={(date) => setDateRanges((prev) => ({ ...prev, clientStartDate: date }))}
          endDate={dateRanges.clientEndDate}
          setEndDate={(date) => setDateRanges((prev) => ({ ...prev, clientEndDate: date }))}
          onSelect={handleDateRangeSelect}
        />

        <div className="col-md-12 text-center my-2">
          <span className="px-4 py-1 bg-gray-100 rounded-full text-gray-600 text-sm">Or</span>
        </div>

        <DateRangePicker
          label="Expert Deadline"
          startDate={dateRanges.expertStartDate}
          setStartDate={(date) => setDateRanges((prev) => ({ ...prev, expertStartDate: date }))}
          endDate={dateRanges.expertEndDate}
          setEndDate={(date) => setDateRanges((prev) => ({ ...prev, expertEndDate: date }))}
          onSelect={handleDateRangeSelect}
        />

        <div className="col-md-12 text-center my-2">
          <span className="px-4 py-1 bg-gray-100 rounded-full text-gray-600 text-sm">Or</span>
        </div>

        <DateRangePicker
          label="Order Creation"
          startDate={dateRanges.leadStartDate}
          setStartDate={(date) => setDateRanges((prev) => ({ ...prev, leadStartDate: date }))}
          endDate={dateRanges.leadEndDate}
          setEndDate={(date) => setDateRanges((prev) => ({ ...prev, leadEndDate: date }))}
          onSelect={handleDateRangeSelect}
        />

        <div className="col-md-12 text-center my-2">
          <span className="px-4 py-1 bg-gray-100 rounded-full text-gray-600 text-sm">And</span>
        </div>

        <ResultDropdown value={resultFilter} onChange={setResultFilter} />

        <div className="col-md-12 text-center mt-4 mb-2">
          <Buttons
            title="Apply Filter"
            onClick={applyFilter}
            className="btn-primary shadow-sm hover:shadow-md transition-shadow"
          />
          <button
            className="btn btn-sm btn-danger ml-2 shadow-sm hover:shadow-md transition-shadow"
            onClick={clearFilter}
          >
            Clear Filter
          </button>
        </div>
      </div>
    </Fragment>
  )

  return (
    <Fragment>
      <CustomModal onClose={() => setIsDateFilter(false)} size="md" open={isDateFilter} content={Content} />
      <span
        className="filter-icon d-inline-flex align-items-center justify-content-center p-2 rounded-circle bg-light cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={() => setIsDateFilter(!isDateFilter)}
        title="Open Filter"
      >
        <Icons color="#00234a" title="date-filter" />
      </span>
    </Fragment>
  )
}

export default RangeDateFilter
